import React, { FC, useState } from 'react'
import Modal from './Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Carousel from './Carousel'
import { useDispatch } from 'react-redux'
import { updateServiceRequest } from '../../../states/containers/common/commonPartSlice'

interface Props {
  display: boolean,
  closeCallback: () => void
}

interface Step {
  step: number
  icon: string
  title: string
  description: string
  active: boolean
}

interface Sample {
  title_1: string
  title_2: string
  description: string
  images: string[]
}

interface OutPut {
  title: string
  description: string[]
  example: string
}

interface Suggest {
  target: string
  suggest: string
  description: string[]
}

const STEPS: Step[] = [
  {
    step: 1,
    icon: "/static/images/analyze_support/icon_1.png",
    title: "データ抽出",
    description: "Qaseeで自動取得した作業データや業務仕分けデータを分析の基礎データとします。",
    active: false,
  },
  {
    step: 2,
    icon: "/static/images/analyze_support/icon_2.png",
    title: "現状分析",
    description: "部門別・業務別・個人別のQaseeデータから構造やパターンを判別、分析を実施します。",
    active: true,
  },
  {
    step: 3,
    icon: "/static/images/analyze_support/icon_3.png",
    title: "課題抽出",
    description: "業務の無駄や偏り、属人化、ボトルネックを抽出し、課題となっている構造を分解。 さらに示唆分析を行います。",
    active: true,
  },
  {
    step: 4,
    icon: "/static/images/analyze_support/icon_4.png",
    title: "施策提案",
    description: "優先度・難易度つきの具体的な改善策を提示。削減効果の試算まで可視化します。",
    active: true,
  },
  {
    step: 5,
    icon: "/static/images/analyze_support/icon_5.png",
    title: "指標整理",
    description: "改善の成果指標となるトラッキングすべき項目をご提供します。",
    active: true,
  },
]

const SAMPLES: Sample[] = [
  {
    title_1: "基本フレーム",
    title_2: "概要・現状分析・考察",
    description: "分析の骨格。全社での位置づけから、状態と論点の言語化",
    images: [
      "/static/images/analyze_support/carousel_1_1.png",
      "/static/images/analyze_support/carousel_1_2.png",
      "/static/images/analyze_support/carousel_1_3.png",
      "/static/images/analyze_support/carousel_1_4.png",
      "/static/images/analyze_support/carousel_1_5.png",
      "/static/images/analyze_support/carousel_1_6.png",
      "/static/images/analyze_support/carousel_1_7.png",
    ],
  },
  {
    title_1: "基本フレーム",
    title_2: "施策案・評価基準・成果指標",
    description: "打ち手の具体化、評価フレーム、そして毎月の成果指標",
    images: [
      "/static/images/analyze_support/carousel_2_1.png",
      "/static/images/analyze_support/carousel_2_2.png",
      "/static/images/analyze_support/carousel_2_3.png",
    ],
  },
  {
    title_1: "分析シート",
    title_2: "残業分析・付加価値分析・自動化分析",
    description: "残業や稼働率、コア/ノンコア業務分類、業務フロー自動化代替候補の深堀り",
    images: [
      "/static/images/analyze_support/carousel_3_1.png",
      "/static/images/analyze_support/carousel_3_2.png",
      "/static/images/analyze_support/carousel_3_3.png",
    ],
  },
  {
    title_1: "部署 部門分析シート",
    title_2: "部署別現状分析・部署考察・課題示唆・施策フィードバック",
    description: "部署ごとに、現状評価、業務課題特定、施策案を提示。",
    images: [
      "/static/images/analyze_support/carousel_4_1.png",
      "/static/images/analyze_support/carousel_4_2.png",
    ],
  },
  {
    title_1: "個人別分析シート",
    title_2: "現状分析・個人考察・施策フィードバック",
    description: "メンバーごとに働き方・業務内容・FB案を1シートに集約。",
    images: [
      "/static/images/analyze_support/carousel_5_1.png",
      "/static/images/analyze_support/carousel_5_2.png",
      "/static/images/analyze_support/carousel_5_3.png",
    ],
  },
]

const OUTPUTS: OutPut[] = [
  {
    title: "改善優先順位",
    description: [
      "効果×実現性でマッピングし、着手順を提示。",
      "まず『実現難易度・低×改善効果・大』から。",
    ],
    example: "例：定型事務の自動化を最優先",
  },
  {
    title: "削減効果試算",
    description: [
      "施策ごとに対象工数の目安を定量化。",
      "目標達成に必要な削減量との差分を可視化。",
    ],
    example: "例：ノンコア428h → 目標238hをカバー",
  },
  {
    title: "自動化候補",
    description: [
      "作業タイプ別に自動化・AI代替の宛先を特定。",
      "導入検討システム例まで具体的に提示。",
    ],
    example: "例：請求処理 → Form + GAS自動転記",
  },
  {
    title: "属人化リスク",
    description: [
      "特定個人への業務・残業の偏り等を検出。",
      "再分配・標準化の対象を早期に把握。",
    ],
    example: "例：残業が突出して高いメンバーの業務再分配",
  },
]

const SUGGESTS: Suggest[] = [
  {
    target: "経営者",
    suggest: "意思決定",
    description: [
      "部門間の工数配分・削減余地を横断で把握。",
      "全社目標（例：▲20%）の達成道筋を数字で描く。",
    ],
  },
  {
    target: "管理職",
    suggest: "業務改善",
    description: [
      "どの業務を・どう削るかが優先度つきで明確に。",
      "会議棚卸しや自動化など具体アクションに直結。",
    ],
  },
  {
    target: "現　場",
    suggest: "生産性向上",
    description: [
      "業務の負荷や課題を明確化し、業務改善を実行。",
      "空いた時間をコア業務へ再配分できる。",
    ],
  },
]

const AnalyzeSupportModal: FC<Props> = (props) => {
  const dispatch = useDispatch()

  const {
    display,
    closeCallback,
  } = props

  const [isDisplayRequest, setIsDisplayRequest] = useState<boolean>(false)
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false)
  const [isRequestSuccess, setIsRequestSuccess] = useState<boolean>(false)

  const closeModal = () => {
    setIsDisplayRequest(false)
    setIsRequestSuccess(false)
    setIsRequestLoading(false)
    closeCallback()
  }

  const submit = () => {
    setIsRequestLoading(true)
    dispatch(
      updateServiceRequest(
        {
          request_type: "データ分析支援",
        },
        () => {
          setIsRequestSuccess(true)
          setIsRequestLoading(false)
        },
      ),
    )
  }

  const buildStep = (item: Step) => (
    <React.Fragment key={`step_${item.step}`}>
      <div
        style={{
          position: "relative",
          backgroundColor: item.active ? "#4472BA1A" : "#F5F5F5",
          padding: "24px",
          display: "flex",
          alignItems: "center",
          gap: "24px",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: "24px",
            color: item.active ? "#4472BA" : "#999999",
          }}
        >
          {`Step.${item.step}`}
        </div>
        {
          item.active && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: "-16px",
                transform: "translateY(-50%)",
                backgroundColor: "#4472BA",
                color: "#FFFFFF",
                padding: "8px 16px",
                fontSize: "16px",
              }}
            >
              データ分析支援
            </div>
          )
        }
        <div
          style={{
            width: "60px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            alt=""
            src={item.icon}
            style={{
              height: "60px",
            }}
          />
        </div>
        <div
          style={{
            width: "220px",
            fontSize: "34px",
            color: item.active ? "#4472BA" : "#666666",
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            width: "calc(100% - 328px)",
            fontSize: "20px",
          }}
        >
          {item.description}
        </div>
      </div>
      {
        STEPS.length > item.step && (
          <div
            style={{
              color: item.active ? "#4472BA1A" : "#F5F5F5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "60px",
            }}
          >
            <FontAwesomeIcon icon={["fas", "caret-down"]} />
          </div>
        )
      }
    </React.Fragment>
  )

  const buildSample = (item: Sample) => (
    <React.Fragment key={`output_${item.title_1}_${item.title_2}`}>
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          gap: "24px",
          marginBottom: "40px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexFlow: "column",
            gap: "24px",
          }}
        >
          <div
            style={{
              fontSize: "24px",
            }}
          >
            {`${item.title_1}：`}
            <span style={{ color: "#4472BA" }}>
              {item.title_2}
            </span>
          </div>
          <div
            style={{
              fontSize: "18px",
            }}
          >
            {item.description}
          </div>
        </div>
        <div>
          <Carousel
            images={item.images}
          />
        </div>
      </div>
    </React.Fragment>
  )

  const buildOutPut = (item: OutPut) => (
    <React.Fragment key={`output_${item.title}`}>
      <div
        style={{
          width: "calc(50% - 18px)",
          display: "flex",
          flexFlow: "column",
          gap: "16px",
          border: "solid 1px #4472BA",
          borderRadius: "4px",
          padding: "16px",
        }}
      >
        <div
          style={{
            fontSize: "30px",
            fontWeight: "bold",
            color: "#4472BA",
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            fontSize: "18px",
            display: "flex",
            flexFlow: "column",
            gap: "8px",
          }}
        >
          {
            item.description.map((v) => (
              <div key={`${item.title}_${v}`}>
                {v}
              </div>
            ))
          }
        </div>
        <div
          style={{
            backgroundColor: "#4472BA1A",
            padding: "8px",
            fontSize: "18px",
            fontWeight: "bold",
          }}
        >
          {item.example}
        </div>
      </div>
    </React.Fragment>
  )

  const buildSuggest = (item: Suggest) => (
    <React.Fragment key={`suggest_${item.target}`}>
      <div
        style={{
          border: "solid 1px #707070",
          borderRadius: "4px",
          display: "flex",
          gap: "64px",
          padding: "40px",
        }}
      >
        <div
          style={{
            fontSize: "30px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "32px",
          }}
        >
          <div
            style={{
              width: "100px",
            }}
          >
            {item.target}
          </div>
          <FontAwesomeIcon icon={['fas', 'play']} style={{ color: "#4472BA" }} />
          <div
            style={{
              color: "#4472BA",
              width: "200px",
            }}
          >
            {item.suggest}
          </div>
        </div>
        <div
          style={{
            fontSize: "24px",
            display: "flex",
            flexFlow: "column",
            gap: "16px",
          }}
        >
          {
            item.description.map((v) => (
              <div key={`${item.target}_${v}`}>
                {v}
              </div>
            ))
          }
        </div>
      </div>
    </React.Fragment>
  )

  const requestButton = () => (
    <button
      type="button"
      style={{
        width: "620px",
        backgroundColor: "#4472BA",
        border: "solid 3px #FFFFFF",
        borderRadius: "12px",
        color: "#FFFFFF",
        boxShadow: "4px 4px 6px #CCCCCC",
        padding: "24px 40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={() => setIsDisplayRequest(true)}
    >
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "50px",
          fontSize: "22px",
          color: "#4472BA",
          padding: "8px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
        }}
      >
        データ分析支援のお見積り・ご相談はこちら
        <FontAwesomeIcon icon={['fas', 'play']} style={{ fontSize: "16px" }} />
      </div>
    </button>
  )

  return (
    <>
      <Modal
        modalSize="lp"
        show={display}
        closeCallback={closeModal}
        closeable
        wrapperClass=""
      >
        <>
          <div
            style={{
              userSelect: "none",
              color: "#666666",
            }}
          >
            <div
              style={{
                position: "relative",
                width: "calc(100% + 64px)",
                height: "400px",
                margin: "-32px -32px 0",
                backgroundColor: "#4472BA1A",
                display: "flex",
                flexFlow: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "40px",
              }}
            >
              <div
                style={{
                  fontSize: "26px",
                }}
              >
                「
                <span style={{ color: "#4472BA" }}>分析する</span>
                時間」を「
                <span style={{ color: "#4472BA" }}>改善する</span>
                時間へ」
              </div>
              <div
                style={{
                  fontSize: "50px",
                  fontWeight: "bold",
                  color: "#4472BA",
                }}
              >
                データ分析支援
              </div>
              <div
                style={{
                  fontSize: "20px",
                  display: "flex",
                  flexFlow: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  letterSpacing: "0.05em",
                  color: "#777777",
                }}
              >
                <div>
                  属人的な集計・分析の負荷を取り除き、
                </div>
                <div>
                  意思決定と改善のスピードを底上げします。
                </div>
              </div>
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  bottom: 0,
                  transform: "translate(-50%, 30%)",
                }}
              >
                {requestButton()}
              </div>
              <img
                alt=""
                src="/static/images/analyze_support/image_1.png"
                style={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  height: "390px",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexFlow: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "40px",
                marginTop: "80px",
              }}
            >
              <div
                style={{
                  fontSize: "34px",
                  fontWeight: "bold",
                }}
              >
                Qaseeが分析から改善までを、一気通貫で実施。
              </div>
              <div
                style={{
                  display: "flex",
                  flexFlow: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "16px",
                  fontSize: "24px",
                }}
              >
                <div>
                  Qaseeで取得した作業データや工数管理データ等の大規模データを基に、
                </div>
                <div>
                  <span style={{ color: "#4472BA" }}>現状分析 → 考察 → 施策提案 → 改善効果検証</span>
                  まで一気通貫で実施します。
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: "80px",
                padding: "0 40px",
                display: "flex",
                flexFlow: "column",
                gap: "16px",
              }}
            >
              {STEPS.map((v) => buildStep(v))}
            </div>
            <div
              style={{
                marginTop: "80px",
                padding: "0 40px",
                display: "flex",
                flexFlow: "column",
                gap: "40px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "30px",
                }}
              >
                【分析レポート サンプルフォーマット】
              </div>
              <div
                style={{
                  display: "flex",
                  flexFlow: "column",
                  gap: "8px",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                }}
              >
                <div>
                  ※本資料はあくまで一例です。実際には御社が抱える課題や明らかにしたいテーマをヒアリングしたうえで、
                </div>
                <div>
                  分析内容を個別に設計・カスタマイズして、御社に最適な分析レポートをご提供いたします。
                </div>
              </div>
              {SAMPLES.map((v) => buildSample(v))}
            </div>
            <div
              style={{
                marginTop: "80px",
                padding: "0 40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexFlow: "column",
                gap: "16px",
              }}
            >
              {requestButton()}
            </div>
            <div
              style={{
                marginTop: "80px",
                padding: "0 40px",
                display: "flex",
                flexFlow: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "40px",
              }}
            >
              <div
                style={{
                  fontSize: "34px",
                  fontWeight: "bold",
                  color: "#4472BA",
                }}
              >
                示唆にとどまらず、打ち手まで踏み込む
              </div>
              <div
                style={{
                  fontSize: "24px",
                }}
              >
                単なる集計を超えて、コンサルタント視点でのアウトプットを提示します。
              </div>
              <div
                style={{
                  display: "flex",
                  flexFlow: "column",
                  gap: "8px",
                  width: "80%",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                  }}
                >
                  ※アウトプットの一例
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "36px",
                  }}
                >
                  {OUTPUTS.map((v) => buildOutPut(v))}
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: "80px",
                padding: "0 40px",
                display: "flex",
                flexFlow: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "40px",
              }}
            >
              <div
                style={{
                  fontSize: "34px",
                  fontWeight: "bold",
                  color: "#4472BA",
                }}
              >
                3つの階層すべてに、意味のある示唆を
              </div>
              <div
                style={{
                  fontSize: "24px",
                }}
              >
                同じ工数データから、立場ごとに異なる価値を引き出します。
              </div>
              <div
                style={{
                  display: "flex",
                  flexFlow: "column",
                  gap: "32px",
                  width: "80%",
                }}
              >
                {SUGGESTS.map((v) => buildSuggest(v))}
              </div>
            </div>
            <div
              style={{
                width: "calc(100% + 64px)",
                margin: "80px -32px -32px",
                padding: "40px 40px 160px",
                display: "flex",
                flexFlow: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "40px",
                backgroundColor: "#4472BA1A",
              }}
            >
              <div
                style={{
                  fontSize: "34px",
                  fontWeight: "bold",
                  color: "#4472BA",
                }}
              >
                企業の持続的な成長をデータで支援します
              </div>
              <div
                style={{
                  fontSize: "24px",
                  display: "flex",
                  flexFlow: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                <div>
                  Qaseeのデータ分析支援サービスは、蓄積された工数データを分析し、
                </div>
                <div>
                  「経営判断」と「現場改善」につながる具体的なアクションをご提案します。
                </div>
                <div>
                  可視化するだけで終わらせず、業務のムダ削減と生産性向上を実現します。
                </div>
              </div>
              <img
                alt=""
                src="/static/images/analyze_support/image_2.png"
                style={{
                  width: "50%",
                }}
              />
              {requestButton()}
            </div>
          </div>
        </>
      </Modal>
      <Modal
        show={isDisplayRequest}
        closeCallback={() => setIsDisplayRequest(false)}
        closeable
        wrapperClass=""
      >
        <>
          {
            isRequestSuccess ? (
              <>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "24px",
                  }}
                >
                  <div>
                    お見積り・ご相談リクエストを受け付けました。
                  </div>
                  <div>
                    Qasee カスタマーサクセスより順次ご連絡致します。
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "16px",
                    gap: "8px",
                  }}
                >
                  <button
                    type="button"
                    className="button cancel"
                    onClick={closeModal}
                    style={{
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #656E73",
                      color: "#656E73",
                      width: "200px",
                      textAlign: "center",
                      borderRadius: "4px",
                    }}
                  >
                    閉じる
                  </button>
                </div>
              </>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "24px",
                  }}
                >
                  <div>
                    お見積り・ご相談リクエストを送信します。
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "16px",
                    gap: "8px",
                  }}
                >
                  <button
                    type="button"
                    className="button cancel"
                    onClick={() => setIsDisplayRequest(false)}
                    style={{
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #656E73",
                      color: "#656E73",
                      width: "200px",
                      textAlign: "center",
                      borderRadius: "4px",
                    }}
                  >
                    キャンセル
                  </button>
                  <button
                    type="button"
                    className="button"
                    onClick={submit}
                    style={{
                      backgroundColor: "#1630c6",
                      border: "1px solid #1630c6",
                      color: "#FFFFFF",
                      width: "200px",
                      textAlign: "center",
                      borderRadius: "4px",
                    }}
                    disabled={isRequestLoading}
                  >
                    {
                      isRequestLoading ? (
                        <>
                          <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                        </>
                      ) : (
                        <>
                          リクエストを送信する
                        </>
                      )
                    }
                  </button>
                </div>
              </>
            )
          }
        </>
      </Modal>
    </>
  )
}

export default AnalyzeSupportModal
